import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import classNames from "classnames";
import { DIALOG_BUTTON_BORDER_RADIUS, DIALOG_BUTTON_MIN_HEIGHT } from "./constants";
import ErrorDialog from "./ErrorDialog";

/**
 * Get area title text from title config for dialog header
 */
export function getAreaTitleText(titleConfig) {
  if (!titleConfig) return "";
  const { type, text, areaSplit } = titleConfig;
  if (
    type === "coverHeader" ||
    type === "winchester" ||
    type === "demoAccount" ||
    type === "subdivision"
  ) {
    return text || "";
  }
  if (type === "areaSplit" && areaSplit && areaSplit.length) {
    return areaSplit.join(" & ");
  }
  return "";
}

const useStyles = makeStyles({
  dialogPaper: {
  },
  editDialogHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px 24px",
    textAlign: "center",
  },
  editDialogTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: "12px",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  editDialogDivider: {
    width: "100%",
    height: "1px",
    backgroundColor: "white",
    marginBottom: "12px",
  },
  editDialogInstruction: {
    fontSize: "17px",
    lineHeight: "1.5",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  editDialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    alignItems: "center",
  },
  editDialogBodyPadding: {
    padding: "24px",
  },
  actionButtonWrap: {
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  tableFullBox: {
    width: "100%",
    maxWidth: "400px",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    padding: "12px 0",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    cursor: "not-allowed",
    boxSizing: "border-box",
  },
  tableFullMessage: {
    width: "100%",
    maxWidth: "400px",
    fontSize: "14px",
    color: "#b91c1c",
    fontFamily: "objektiv-mk2, sans-serif",
    animation: "$tableFullFlash 1.2s ease-in-out infinite",
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    padding: "0 12px",
    boxSizing: "border-box",
  },
  tableFullMessageIcon: {
    flexShrink: 0,
    marginTop: "2px",
    color: "#b91c1c",
  },
  "@keyframes tableFullFlash": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.35 },
  },
  cancelWrap: {
    display: "flex",
    justifyContent: "flex-start",
    padding: "0 24px 24px 24px",
  },
});

export default function EditTableButtonsDialog({
  open,
  onClose,
  titleConfig,
  propertyCount = 0,
  maxProperties = 12,
  onEditProperty,
  onDeleteProperty,
  onAddProperty,
  onViewEntirePropertyList,
  onReorderProperties,
  onEditTableFooter,
}) {
  const classes = useStyles();
  const areaTitle = getAreaTitleText(titleConfig) || "EDIT TABLE";
  const isTableFull = propertyCount >= maxProperties;
  const [tableFullDialogOpen, setTableFullDialogOpen] = useState(false);

  const handleEdit = () => {
    onEditProperty?.();
    onClose?.();
  };
  const handleDelete = () => {
    onDeleteProperty?.();
    onClose?.();
  };
  const handleAdd = () => {
    if (isTableFull) {
      setTableFullDialogOpen(true);
      return;
    }
    onAddProperty?.();
    onClose?.();
  };
  const handleTableFullCancel = () => {
    setTableFullDialogOpen(false);
  };
  const handleTableFullOk = () => {
    setTableFullDialogOpen(false);
    onDeleteProperty?.();
    onClose?.();
  };

  const handleReorder = () => {
    onReorderProperties?.();
    onClose?.();
  };
  const handleViewEntirePropertyList = () => {
    onViewEntirePropertyList?.();
    onClose?.();
  };
  const handleEditTableFooter = () => {
    onEditTableFooter?.();
    onClose?.();
  };
  return (
    <>
      <ErrorDialog
        open={tableFullDialogOpen}
        onClose={handleTableFullCancel}
        headerText="PLEASE NOTE:"
        contentText={`The table is full with ${maxProperties} properties.\nYou must delete a property\nin order to add another one.`}
        headerVariant="blue"
        footerSplit
        bodyAlign="center"
        dialogProps={{ style: { zIndex: 3000 } }}
        buttons={[
          { label: "Cancel", onClick: handleTableFullCancel },
          { label: "OK", onClick: handleTableFullOk },
        ]}
      />
      <MailProDialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
        <MailProDialogHeader>
          <div className={classes.editDialogHeader}>
            <div className={classes.editDialogTitle}>{areaTitle}</div>
            <div className={classes.editDialogDivider} />
            <div className={classes.editDialogInstruction}>
              <div>When EDITING the table,</div>
              <div>select one of the options below:</div>
            </div>
          </div>
        </MailProDialogHeader>
        <MailProDialogBody className={classNames(classes.editDialogBody, classes.editDialogBodyPadding)}>
          <div className={classes.actionButtonWrap}>
            <MailProButton
              variant="filled"
              label="ADD a Property"
              onClick={handleAdd}
              customStyle={{
                width: "100%",
                fontSize: "16px",
                padding: "10px 24px",
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
            />
          </div>
          <div className={classes.actionButtonWrap}>
            <MailProButton
              variant="filled"
              label="DELETE a Property"
              onClick={handleDelete}
              customStyle={{
                width: "100%",
                fontSize: "16px",
                padding: "10px 24px",
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
            />
          </div>
          <div className={classes.actionButtonWrap}>
            <MailProButton
              variant="filled"
              label="EDIT a Property"
              onClick={handleEdit}
              customStyle={{
                width: "100%",
                fontSize: "16px",
                padding: "10px 24px",
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
            />
          </div>
          <div className={classes.actionButtonWrap}>
            <MailProButton
              variant="filled"
              label="REORDER / SORT Properties"
              onClick={handleReorder}
              customStyle={{
                width: "100%",
                fontSize: "16px",
                padding: "10px 24px",
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
            />
          </div>
          <div className={classes.actionButtonWrap}>
            <MailProButton
              variant="filled"
              label="View Entire Property List"
              onClick={handleViewEntirePropertyList}
              customStyle={{
                width: "100%",
                fontSize: "16px",
                padding: "10px 24px",
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
            />
          </div>
          <div className={classes.actionButtonWrap}>
            <MailProButton
              variant="filled"
              label="EDIT TABLE FOOTER"
              onClick={handleEditTableFooter}
              customStyle={{
                width: "100%",
                fontSize: "16px",
                padding: "10px 24px",
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
            />
          </div>
        </MailProDialogBody>
        <div className={classes.cancelWrap}>
          <MailProButton
            variant="filled"
            label="Cancel"
            onClick={onClose}
            customStyle={{
              fontSize: "12px",
              padding: "6px 14px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
            }}
          />
        </div>
      </MailProDialog>
    </>
  );
}
